import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { combineLatest, Observable, of } from 'rxjs';
import { map, mapTo, shareReplay, take } from 'rxjs/operators';

import {
    CustomHttpParamEncoder,
    Group,
    GroupId,
    mapToPaginatedResult,
    OrganisationId,
    PaginatedResults,
    PaginatedUser,
    PaginationInfo,
    QueryData,
    SearchState,
    ShAbstractHttpService,
    ShApiCachingHelper,
    ShPaginationService,
    SortInfo,
    User,
    UserGroupType,
    UserId
} from '@stylehaven/components';
import { CreateGroup } from '../../entities/mediahaven-api/groups/create-group.entity';

@Injectable({ providedIn: 'root' })
export class GroupsService {
    private readonly defaultHeaders: HttpHeaders = new HttpHeaders();
    private readonly groupsEndpoint: string = 'v2/groups';
    private groups$: Observable<Group[]>;

    constructor(
        private readonly paginationService: ShPaginationService,
        private apiCachingHelper: ShApiCachingHelper,
        private readonly http: ShAbstractHttpService
    ) {}

    // #region GET
    public getGroupList(searchState: SearchState, organisationId: OrganisationId): Observable<PaginatedResults<Group>> {
        const sortInfo: SortInfo = searchState.sortInfo;
        const paginationInfo: PaginationInfo = {
            page: searchState.page,
            resultsPerPage: searchState.pageSize
        };

        let params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() })
            .set('sort', sortInfo.field)
            .set('direction', sortInfo.direction);

        for (const type of [
            'Normal',
            'Public',
            'Global',
            'System',
            'Administrator',
            'Organisation'
        ] as UserGroupType[]) {
            params = params.append('type', type);
        }

        if (organisationId) {
            params = params.set('organisationId', organisationId);
        }

        searchState.structuredQuery.Fields?.forEach(queryPart => {
            if (queryPart.values.length > 0) {
                let wildcardSearch = false;
                const field: string = queryPart.field.replace('*', () => {
                    wildcardSearch = true;
                    return '';
                });

                let value: string = queryPart.values.join('');
                if (wildcardSearch) {
                    value = `*${value}*`;
                }

                params = params.set(field, value);
            }
        });

        const startIndex: number = paginationInfo.page * paginationInfo.resultsPerPage;

        return this.paginationService
            .get<Group>(this.groupsEndpoint, params, this.defaultHeaders, startIndex, paginationInfo.resultsPerPage)
            .pipe(map(results => mapToPaginatedResult(results, paginationInfo.resultsPerPage)));
    }

    public getGroupById(groupId: GroupId): Observable<Group> {
        return this.http.get<Group>(`${this.groupsEndpoint}/${groupId}`);
    }

    public getGroupsByOrganisationId(organisationId?: OrganisationId): Observable<Group[]> {
        let params: HttpParams = new HttpParams();
        if (organisationId) {
            params = params.set('organisationId', organisationId);
        }

        return this.apiCachingHelper
            .cachedApiCall(
                this.paginationService
                    .getAll<Group>(this.groupsEndpoint, params)
                    .pipe(map(groups => groups.filter(group => group.Type !== UserGroupType.Zone)))
            )
            .pipe(take(1));
    }

    public getAllGroupUsers(groupId: GroupId): Observable<User[]> {
        const url = `${this.groupsEndpoint}/${groupId}/users`;
        return this.apiCachingHelper.cachedApiCall(this.paginationService.getAll<User>(url));
    }

    public getPaginatedGroupUsers(groupId: GroupId, queryData: QueryData): Observable<PaginatedResults<PaginatedUser>> {
        const sortInfo: SortInfo = queryData.sortInfo;
        const paginationInfo: PaginationInfo = queryData.paginationInfo;
        const startIndex: number = paginationInfo.page * paginationInfo.resultsPerPage;
        const filterValue: string = (queryData.structuredQuery.Fields ?? [])
            .map(filter => filter.values.map(value => `*${value}*`))
            .join('');

        const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() })
            .set('name', filterValue)
            .set('sort', sortInfo.field)
            .set('direction', sortInfo.direction);

        const url = `${this.groupsEndpoint}/${groupId}/users`;

        return this.paginationService
            .get<PaginatedUser>(url, params, this.defaultHeaders, startIndex, paginationInfo.resultsPerPage)
            .pipe(
                map(results => ({
                    ...results,
                    Results: results.Results.map(user => new PaginatedUser(user))
                }))
            )
            .pipe(map(results => mapToPaginatedResult(results, paginationInfo.resultsPerPage)));
    }

    public getUserCountByGroupId(groupId: GroupId): Observable<number> {
        const url = `${this.groupsEndpoint}/${groupId}/users`;
        const params: HttpParams = new HttpParams();
        return this.apiCachingHelper
            .cachedApiCall(this.paginationService.get(url, params, this.defaultHeaders, 0, 2))
            .pipe(map(result => result.TotalNrOfResults));
    }

    // #endregion

    // #region POST

    public createGroup(groupData: CreateGroup): Observable<Group> {
        return this.http.post<Group>(this.groupsEndpoint, groupData).pipe(shareReplay(1));
    }

    public addUsersToGroup(groupId: GroupId, userIds: UserId[]): Observable<boolean[]> {
        if (userIds.length === 0) {
            return of([true]);
        }

        const requestStreams: Observable<boolean>[] = [];
        const request = `${this.groupsEndpoint}/${groupId}/users`;
        // limited to 20 users per request
        for (let start = 0; start < userIds.length; start += 20) {
            requestStreams.push(this.http.post<void>(request, userIds.slice(start, start + 20)).pipe(mapTo(true)));
        }
        return combineLatest(requestStreams);
    }

    // #endregion

    // #region PUT

    public updateGroup(groupData: Group): Observable<Group> {
        const url = `${this.groupsEndpoint}/${groupData.Id}`;
        return this.http.put<Group>(url, groupData).pipe(take(1), shareReplay(1));
    }

    // #endregion

    // #region DELETE

    public deleteGroup(groupId: GroupId): Observable<boolean> {
        const url = `${this.groupsEndpoint}/${groupId}`;
        return this.http.delete<boolean>(url).pipe(take(1), shareReplay(1));
    }

    public removeUsersFromGroup(groupId: GroupId, userIds: UserId[]): Observable<boolean[]> {
        if (userIds.length === 0) {
            return of([true]);
        }

        const requestStreams: Observable<boolean>[] = [];
        const request = `${this.groupsEndpoint}/${groupId}/users`;
        // limited to 20 users per request
        for (let start = 0; start < userIds.length; start += 20) {
            const userSubset: UserId[] = userIds.slice(start, start + 20);
            requestStreams.push(this.http.delete<boolean>(request, { body: userSubset }).pipe(mapTo(true)));
        }
        return combineLatest(requestStreams);
    }

    // #endregion
}
